/**
 * PlansTree — the file/folder tree rows for the Plans panel: file rows,
 * recursive folder nodes (nested any depth), the multi-select destination
 * picker, and the blue "has takeoffs" dot. Kept in its own module so the
 * panel file stays small.
 *
 * NOTE: the recursion below uses createElement on purpose — a JSX
 * self-reference (<PlanFolderNode> inside PlanFolderNode) makes the dev
 * visual-edits babel plugin recurse forever and overflow the compiler.
 */
import { createElement } from "react";
import { CheckSquare, ChevronDown, ChevronRight, FileUp, FolderPlus, MoreVertical, Pencil, Square, Trash2, X } from "lucide-react";
import { INPUT_SM, MONO, fmtSize, isPreviewable } from "./theme";

/** Small blue dot = this file/page has saved takeoffs (tooltip + aria label
 * so the meaning never depends on colour alone). */
export const TakeoffDot = ({ testid, label = "Has takeoffs" }) => (
  <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" role="img" aria-label={label} title={label} data-testid={testid} />
);

export function PlanFileRow({
  d, depth = 0, drawing, renaming, setRenaming, commitRename,
  multiMode, sel, toggleSel, startDrag, fileHasTakeoffs,
  openDrawing, moveId, setMoveId, execMove, folders, folderDepth, setDelFile,
  viewer = false,
}) {
  const checked = sel.has(`d:${d.drawing_id}`);
  return (
    <div
      draggable={!renaming && !viewer}
      onDragStart={(e) => startDrag(e, `d:${d.drawing_id}`)}
      className={`group flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${drawing?.drawing_id === d.drawing_id ? "border-[#f97316] bg-orange-50/60" : "border-transparent hover:border-slate-200 hover:bg-slate-50"} ${checked ? "bg-blue-50/70" : ""}`}
      style={depth ? { marginLeft: depth * 14 } : undefined}
      data-testid={`plan-file-${d.drawing_id}`}
    >
      {multiMode && (
        <button aria-label={`Select ${d.name}`} onClick={() => toggleSel(`d:${d.drawing_id}`)} className="shrink-0 text-slate-400 hover:text-slate-600" data-testid={`sel-d-${d.drawing_id}`}>
          {checked ? <CheckSquare className="h-3.5 w-3.5 text-[#1a3fd1]" /> : <Square className="h-3.5 w-3.5" />}
        </button>
      )}
      {renaming?.kind === "file" && renaming.id === d.drawing_id ? (
        <input autoFocus value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
          onBlur={commitRename}
          onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(null); }}
          className={`${INPUT_SM} h-6 flex-1`} data-testid={`plan-rename-input-${d.drawing_id}`} />
      ) : (
        <button className="min-w-0 flex-1 text-left" onClick={() => openDrawing(d, 1)} data-testid={`plan-open-${d.drawing_id}`}>
          <span className="block truncate text-xs font-medium text-slate-800">{d.name}</span>
          <span className="block text-[10px] text-slate-400">{fmtSize(d.size)}{!isPreviewable(d) ? " · download only" : ""}{d.optimizing ? <span className="ml-1 rounded bg-orange-100 px-1 font-semibold uppercase tracking-wider text-orange-700" data-testid={`plan-optimizing-${d.drawing_id}`}>optimizing…</span> : null}</span>
        </button>
      )}
      {fileHasTakeoffs(d.drawing_id) && <TakeoffDot testid={`takeoff-dot-${d.drawing_id}`} label={`${d.name} has takeoffs`} />}
      <div className="relative flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        {!viewer && (
        <>
        <button aria-label={`Rename ${d.name}`} className="rounded p-1 text-slate-400 hover:bg-slate-200/60" onClick={() => setRenaming({ kind: "file", id: d.drawing_id, name: d.name })} data-testid={`plan-rename-${d.drawing_id}`}><Pencil className="h-3 w-3" /></button>
        <button aria-label={`Move ${d.name}`} className="rounded p-1 text-slate-400 hover:bg-slate-200/60" onClick={() => setMoveId(moveId === d.drawing_id ? null : d.drawing_id)} data-testid={`plan-move-${d.drawing_id}`}><MoreVertical className="h-3.5 w-3.5" /></button>
        <button aria-label={`Delete ${d.name}`} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500" onClick={() => setDelFile(d)} data-testid={`plan-delete-${d.drawing_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </>
        )}
        {moveId === d.drawing_id && (
          <div className="absolute right-0 top-6 z-20 w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-xl" data-testid="plan-move-menu">
            <p className="px-2 py-1 text-[9px] font-semibold uppercase tracking-widest text-slate-400">Move to</p>
            <button className="block w-full rounded px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100" onClick={() => { execMove({ drawingIds: [d.drawing_id], folderIds: [] }, { folderId: null, category: "plans" }); setMoveId(null); }}>Plans root</button>
            {folders.filter((f) => (f.category || "plans") === "plans").map((f) => (
              <button key={f.folder_id} className="block w-full truncate rounded px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100" style={{ paddingLeft: 8 + folderDepth(f) * 10 }} onClick={() => { execMove({ drawingIds: [d.drawing_id], folderIds: [] }, { folderId: f.folder_id, category: f.category || "plans" }); setMoveId(null); }}>{f.name}</button>
            ))}
            <button className="block w-full rounded px-2 py-1 text-left text-xs text-slate-700 hover:bg-slate-100" onClick={() => { execMove({ drawingIds: [d.drawing_id], folderIds: [] }, { folderId: null, category: "documents" }); setMoveId(null); }}>Documents</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PlanFolderNode(props) {
  const {
    f, depth, collapsed, toggleCollapsed, renaming, setRenaming, commitRename,
    multiMode, sel, toggleSel, startDrag, dragOverProps, dropTarget,
    recursiveFileCount, setNewFolder, setFolderName, doUpload, setDelFolder,
    viewer = false,
    filesIn, childFolders, renderFileRow,
  } = props;
  const isCollapsed = collapsed.has(f.folder_id);
  const files = filesIn(f.folder_id, f.category || "plans");
  const kids = childFolders(f.folder_id, f.category || "plans");
  const isDrop = dropTarget === f.folder_id;
  const checked = sel.has(`f:${f.folder_id}`);
  return (
    <div style={depth ? { marginLeft: 10 } : undefined}>
      <div
        draggable={!renaming}
        onDragStart={(e) => startDrag(e, `f:${f.folder_id}`)}
        {...dragOverProps(f.folder_id)}
        className={`flex items-center gap-1 rounded-lg border px-2 py-1.5 ${isDrop ? "border-[#1a3fd1] bg-blue-50 ring-1 ring-[#1a3fd1]" : "border-transparent hover:bg-slate-50"} ${checked ? "bg-blue-50/70" : ""}`}
        data-testid={`plans-folder-${f.folder_id}`}
      >
        {multiMode && (
          <button aria-label={`Select folder ${f.name}`} onClick={() => toggleSel(`f:${f.folder_id}`)} className="shrink-0 text-slate-400 hover:text-slate-600" data-testid={`sel-f-${f.folder_id}`}>
            {checked ? <CheckSquare className="h-3.5 w-3.5 text-[#1a3fd1]" /> : <Square className="h-3.5 w-3.5" />}
          </button>
        )}
        <button onClick={() => toggleCollapsed(f.folder_id)} aria-label={isCollapsed ? `Expand ${f.name}` : `Collapse ${f.name}`} className="rounded p-0.5 text-slate-400 hover:bg-slate-100" data-testid={`plans-folder-toggle-${f.folder_id}`}>
          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {renaming?.kind === "folder" && renaming.id === f.folder_id ? (
          <input autoFocus value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(null); }}
            className={`${INPUT_SM} h-6 flex-1`} data-testid="plans-folder-rename-input" />
        ) : (
          <button onClick={() => toggleCollapsed(f.folder_id)} className="min-w-0 flex-1 truncate text-left text-xs font-semibold text-slate-700">{f.name}</button>
        )}
        <span className={`${MONO} text-[10px] text-slate-400`}>{recursiveFileCount(f.folder_id)}</span>
        <div className="flex items-center gap-0.5 opacity-60 transition-opacity hover:opacity-100">
          {!viewer && (
          <>
          <button aria-label={`New subfolder in ${f.name}`} title="New subfolder" className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" onClick={() => { setNewFolder({ parentId: f.folder_id, category: f.category || "plans" }); setFolderName(""); }} data-testid={`plans-folder-subfolder-${f.folder_id}`}><FolderPlus className="h-3 w-3" /></button>
          <button aria-label={`Rename folder ${f.name}`} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" onClick={() => setRenaming({ kind: "folder", id: f.folder_id, name: f.name })} data-testid={`plans-folder-rename-${f.folder_id}`}><Pencil className="h-3 w-3" /></button>
          <button aria-label={`Upload into ${f.name}`} title={`Upload into ${f.name}`} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600"
            onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.multiple = true; inp.accept = "*/*"; inp.onchange = () => { doUpload([...inp.files], f.folder_id, f.category || "plans"); }; inp.click(); }}
            data-testid={`plans-folder-upload-${f.folder_id}`}><FileUp className="h-3 w-3" /></button>
          <button aria-label={`Delete folder ${f.name}`} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500" onClick={() => setDelFolder(f)} data-testid={`plans-folder-delete-${f.folder_id}`}><Trash2 className="h-3 w-3" /></button>
          </>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <div className="space-y-0.5 pt-0.5">
          {kids.map((k) => createElement(PlanFolderNode, { key: k.folder_id, ...props, f: k, depth: depth + 1 }))}
          {files.map((d) => renderFileRow(d, depth + 1))}
          {!kids.length && !files.length && <p className="px-2 py-1 text-[10px] text-slate-400" style={{ marginLeft: (depth + 1) * 14 }}>Empty folder</p>}
        </div>
      )}
    </div>
  );
}

/** Destination picker for multi-select moves — every valid folder in the
 * job, nested, plus both roots. Invalid targets (the moved folders
 * themselves and their descendants) are shown greyed out. */
export function MovePicker({ payload, invalid, childFolders, onPick, onClose }) {
  const total = payload.drawingIds.length + payload.folderIds.length;
  const destRow = (label, dest, key, depth = 0, disabled = false) => (
    <button key={key} disabled={disabled}
      className={`block w-full truncate rounded px-2 py-1.5 text-left text-xs ${disabled ? "cursor-not-allowed text-slate-300" : "text-slate-700 hover:bg-blue-50 hover:text-[#1a3fd1]"}`}
      style={{ paddingLeft: 8 + depth * 12 }}
      onClick={() => onPick(dest)}
      data-testid={`move-dest-${key}`}>
      {label}
    </button>
  );
  const folderDestRows = (parentId, category, depth) =>
    childFolders(parentId, category).flatMap((f) => [
      destRow(f.name, { folderId: f.folder_id, category }, f.folder_id, depth, invalid.has(f.folder_id)),
      ...folderDestRows(f.folder_id, category, depth + 1),
    ]);
  return (
    <div className="absolute inset-x-2 top-24 z-30 max-h-[65%] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-2xl" data-testid="move-picker">
      <div className="flex items-center justify-between px-1 pb-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          Move {total} item{total === 1 ? "" : "s"} to…
        </p>
        <button aria-label="Close destination picker" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
      </div>
      {destRow("Plans root", { folderId: null, category: "plans" }, "root-plans")}
      {folderDestRows(null, "plans", 1)}
      <div className="my-1 border-t border-slate-100" />
      {destRow("Documents", { folderId: null, category: "documents" }, "root-docs")}
      {folderDestRows(null, "documents", 1)}
    </div>
  );
}
